import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type DashboardPagerProps = {
  children: React.ReactNode;
  autoIntervalMs?: number;
  pauseOnInteractionMs?: number;
  startIndex?: number;
  className?: string;
  showDots?: boolean;
};

const DashboardPager: React.FC<DashboardPagerProps> = ({
  children,
  autoIntervalMs = 7000,
  pauseOnInteractionMs = 60000,
  startIndex = 0,
  className = '',
  showDots = true,
}) => {
  const slides = useMemo(() => React.Children.toArray(children), [children]);
  const [index, setIndex] = useState(Math.max(0, Math.min(startIndex, slides.length - 1)));
  const [pausedUntil, setPausedUntil] = useState<number>(Date.now());
  const touchStartX = useRef<number | null>(null);
  const touchDeltaX = useRef<number>(0);

  const clamp = useCallback((n: number) => {
    if (slides.length === 0) return 0;
    if (n < 0) return 0;
    if (n > slides.length - 1) return slides.length - 1;
    return n;
  }, [slides.length]);

  const next = useCallback(() => setIndex(i => clamp(i + 1)), [clamp]);
  const prev = useCallback(() => setIndex(i => clamp(i - 1)), [clamp]);

  useEffect(() => {
    if (slides.length <= 1) return;
    const id = setInterval(() => {
      if (Date.now() >= pausedUntil) {
        setIndex(i => (i + 1) % slides.length);
      }
    }, autoIntervalMs);
    return () => clearInterval(id);
  }, [slides.length, autoIntervalMs, pausedUntil]);

  const pause = useCallback(() => {
    setPausedUntil(Date.now() + pauseOnInteractionMs);
  }, [pauseOnInteractionMs]);

  const onTouchStart: React.TouchEventHandler<HTMLDivElement> = (e) => {
    pause();
    touchStartX.current = e.touches[0].clientX;
    touchDeltaX.current = 0;
  };

  const onTouchMove: React.TouchEventHandler<HTMLDivElement> = (e) => {
    if (touchStartX.current == null) return;
    touchDeltaX.current = e.touches[0].clientX - touchStartX.current;
  };

  const onTouchEnd: React.TouchEventHandler<HTMLDivElement> = () => {
    if (touchStartX.current == null) return;
    const dx = touchDeltaX.current;
    touchStartX.current = null;
    touchDeltaX.current = 0;
    const threshold = 50;
    if (dx > threshold) prev();
    else if (dx < -threshold) next();
  };

  const onWheel: React.WheelEventHandler<HTMLDivElement> = (e) => {
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
      pause();
      if (e.deltaX > 0) next();
      else prev();
    } else {
      e.preventDefault();
      pause();
    }
  };

  const onKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.key === 'ArrowRight') { pause(); next(); }
    if (e.key === 'ArrowLeft') { pause(); prev(); }
  };

  return (
    <div
      className={`dashboard-pager ${className}`}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onWheel={onWheel}
      onKeyDown={onKeyDown}
      tabIndex={0}
      role="region"
      aria-roledescription="carousel"
    >
      <div className="dashboard-pager-track" style={{ transform: `translateX(-${index * 100}%)` }}>
        {slides.map((slide, i) => (
          <div className="dashboard-pager-slide" key={i} aria-hidden={i !== index}>
            {slide}
          </div>
        ))}
      </div>
      {showDots && slides.length > 1 && (
        <div className="pager-dots" aria-label="Slide navigation">
          {slides.map((_, i) => (
            <button
              key={i}
              className={`pager-dot ${i === index ? 'active' : ''}`}
              onClick={() => { pause(); setIndex(i); }}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default DashboardPager;
